/**
 * Values Section Scroll Animation
 *
 * Dynamically handles values from the CMS.
 * Reads the count from data-values-count attribute.
 *
 * Scroll sequence (1 step per value):
 * - Step 0 (default): Show value 1, all layers violet
 * - Step 1: Show value 2, layer 1 (inner) lights up white
 * - Step 2: Show value 3, layer 2 (middle) lights up white
 * - Step 3: Show value 4, layer 3 (outer) lights up white
 * - Step 4: Show value 5, layers connect (rotate layer 2)
 */

export const values = (): void => {
  const section = document.getElementById('js-values')
  if (!section) {
    console.log('[Values] Section #js-values not found')
    return
  }

  console.log('[Values] Initializing scroll animation')

  // Get the number of values from data attribute
  const valuesCount = parseInt(section.dataset.valuesCount || '0', 10)
  if (valuesCount === 0) {
    console.log('[Values] No values found')
    return
  }

  console.log(`[Values] Found ${valuesCount} values`)

  // Get value elements dynamically
  const valuesArray: HTMLElement[] = []
  for (let i = 1; i <= valuesCount; i++) {
    const valueEl = document.getElementById(`value-${i}`)
    if (valueEl) {
      valuesArray.push(valueEl)
    }
  }

  if (valuesArray.length !== valuesCount) {
    console.log('[Values] Not all value elements found', {
      expected: valuesCount,
      found: valuesArray.length,
    })
    return
  }

  // Get logomark layers
  const layer1 = document.getElementById('logomark-layer-1')
  const layer2 = document.getElementById('logomark-layer-2')
  const layer3 = document.getElementById('logomark-layer-3')

  if (!layer1 || !layer2 || !layer3) {
    console.log('[Values] Logomark layers not found')
    return
  }

  // Get scroll indicator
  const scrollIndicator = document.getElementById('values-scroll-indicator')

  console.log('[Values] All elements found')

  // Colors
  const violetColor = 'rgb(var(--color-violet))'
  const whiteColor = 'rgb(var(--color-white))'

  // One step per value
  const totalSteps = valuesCount

  // Track state
  let lastStep = -1

  const update = (): void => {
    const rect = section.getBoundingClientRect()
    const scrollContainer = section.querySelector('div') as HTMLElement
    if (!scrollContainer) return

    const containerHeight = scrollContainer.offsetHeight
    const viewportHeight = window.innerHeight

    // How far the section top is above the viewport top (positive = scrolled past)
    const scrolled = -rect.top

    // Total scrollable distance within this section
    const maxScroll = containerHeight - viewportHeight

    // Progress 0-1
    const progress = Math.max(0, Math.min(1, scrolled / maxScroll))

    // Fade out scroll indicator based on progress
    if (scrollIndicator) {
      if (progress > 0.05) {
        scrollIndicator.style.opacity = Math.max(0, 1 - progress * 3).toString()
      } else {
        scrollIndicator.style.opacity = '1'
      }
    }

    // Calculate current step (one per value)
    const step = Math.min(totalSteps - 1, Math.floor(progress * totalSteps))

    // Only update on step change
    if (step === lastStep) return
    lastStep = step

    console.log(`[Values] Step: ${step}, Progress: ${progress.toFixed(2)}`)

    // Reset all values to hidden
    valuesArray.forEach((value) => {
      value.style.opacity = '0'
      value.style.pointerEvents = 'none'
    })

    // Reset all layers to violet
    layer1.style.color = violetColor
    layer2.style.color = violetColor
    layer3.style.color = violetColor

    // Reset layer 2 rotation
    layer2.style.transform = 'rotate(0deg)'

    // Show active value
    valuesArray[step].style.opacity = '1'
    valuesArray[step].style.pointerEvents = 'auto'

    // Step 1: Layer 1 (inner) lights up
    if (step >= 1) {
      layer1.style.color = whiteColor
    }

    // Step 2: Layer 2 (middle) lights up
    if (step >= 2) {
      layer2.style.color = whiteColor
    }

    // Step 3: Layer 3 (outer) lights up
    if (step >= 3) {
      layer3.style.color = whiteColor
    }

    // Step 4: Layers connect — rotate layer 2
    if (step >= 4) {
      layer2.style.transform = 'rotate(15deg)'
    }
  }

  // Scroll listener with RAF throttle
  let ticking = false
  const onScroll = (): void => {
    if (!ticking) {
      window.requestAnimationFrame(() => {
        update()
        ticking = false
      })
      ticking = true
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true })

  // Initial update
  update()

  console.log('[Values] Animation ready')
}
